import type { CSSProperties, ReactNode } from 'react';
import { C, DEGRADE_ACTION, MENTHE_PLEINE, OMBRE_ACTION, POLICE_TITRE, TRAIT_COCHE } from '../../theme';

/**
 * Le titre d'un ecran : Outfit en graisse 200, grand (handoff Horizon, § Typographie).
 * La finesse du trait est ce qui donne son calme a l'interface — une graisse plus lourde
 * ferait revenir l'ancienne identite.
 */
export function Titre({ children, taille = 40 }: { children: ReactNode; taille?: number }) {
  return (
    <div
      style={{
        fontFamily: POLICE_TITRE,
        fontWeight: 200,
        fontSize: taille,
        lineHeight: 1.05,
        color: C.encre,
      }}
    >
      {children}
    </div>
  );
}

/** Petite etiquette en capitales : « MAINTENANT · 09:20 », « À NE PAS OUBLIER »… */
export function Etiquette({ children, couleur }: { children: ReactNode; couleur: string }) {
  return (
    <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: 0.8, color: couleur }}>{children}</div>
  );
}

export function SousTitreSection({ children }: { children: ReactNode }) {
  return (
    <div
      style={{
        fontSize: 12,
        fontWeight: 800,
        letterSpacing: 0.8,
        textTransform: 'uppercase',
        color: C.secondaire,
      }}
    >
      {children}
    </div>
  );
}

export function Carte({
  children,
  fond = C.carte,
  bordure = true,
  style,
  onClick,
}: {
  children: ReactNode;
  fond?: string;
  bordure?: boolean;
  style?: CSSProperties;
  onClick?: () => void;
}) {
  return (
    <div
      onClick={onClick}
      style={{
        background: fond,
        border: bordure ? `1px solid ${C.bordure}` : undefined,
        borderRadius: 14,
        padding: '14px 16px',
        cursor: onClick ? 'pointer' : undefined,
        ...style,
      }}
    >
      {children}
    </div>
  );
}

export function BoutonPrincipal({
  children,
  onClick,
  actif = true,
  hauteur = 50,
}: {
  children: ReactNode;
  onClick: () => void;
  actif?: boolean;
  hauteur?: number;
}) {
  return (
    <button
      type="button"
      className="pressable"
      onClick={onClick}
      style={{
        height: hauteur,
        width: '100%',
        border: 'none',
        borderRadius: 14,
        background: actif ? DEGRADE_ACTION : C.inactif,
        boxShadow: actif ? OMBRE_ACTION : 'none',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: 16,
        fontWeight: 800,
        fontFamily: 'inherit',
        color: '#FFFFFF',
        cursor: 'pointer',
      }}
    >
      {children}
    </button>
  );
}

/** Interrupteur des connexions (Gmail, Calendrier, Localisation…). */
export function Bascule({ actif, onChange, libelle }: { actif: boolean; onChange: () => void; libelle: string }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={actif}
      aria-label={libelle}
      onClick={onChange}
      style={{
        width: 46,
        height: 27,
        padding: 0,
        border: 'none',
        borderRadius: 999,
        background: actif ? DEGRADE_ACTION : C.inactif,
        position: 'relative',
        cursor: 'pointer',
        transition: 'background 0.2s',
        flexShrink: 0,
      }}
    >
      <span
        style={{
          width: 21,
          height: 21,
          borderRadius: '50%',
          background: '#FFFFFF',
          position: 'absolute',
          top: 3,
          left: actif ? 22 : 3,
          transition: 'left 0.2s',
          boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
        }}
      />
    </button>
  );
}

/** Case a cocher carree, arrondie — taches et listes de sac. */
export function Case({ cochee, taille = 22 }: { cochee: boolean; taille?: number }) {
  return (
    <span
      style={{
        width: taille,
        height: taille,
        borderRadius: taille > 20 ? 7 : 6,
        border: `2px solid ${cochee ? MENTHE_PLEINE : C.bordureForte}`,
        background: cochee ? MENTHE_PLEINE : 'transparent',
        boxSizing: 'border-box',
        flexShrink: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        transition: 'background 0.18s ease-out, border-color 0.18s ease-out',
      }}
    >
      {cochee ? (
        <svg
          className="coche"
          width={taille > 20 ? 13 : 12}
          height={taille > 20 ? 13 : 12}
          viewBox="0 0 24 24"
          fill="none"
          stroke={TRAIT_COCHE}
          strokeWidth="3"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d="m4.5 12.5 5 5 10-11" />
        </svg>
      ) : null}
    </span>
  );
}

/** Pastille de statut : « 4 propositions à valider », « Rangé », « Reçu ». */
export function Pastille({
  children,
  fond,
  couleur,
  icone,
}: {
  children: ReactNode;
  fond: string;
  couleur: string;
  icone?: ReactNode;
}) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        background: fond,
        color: couleur,
        fontSize: 12,
        fontWeight: 800,
        padding: '4px 10px',
        borderRadius: 999,
      }}
    >
      {icone}
      {children}
    </div>
  );
}

/**
 * Ecran de lancement : la marque qui respire, identique au voile d'index.html.
 * Utilise pendant la verification de session, pour que le voile et l'application se
 * relaient sans que rien ne saute a l'ecran.
 */
export function EcranLancement() {
  return (
    <div
      style={{
        flex: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        // Le voile d'index.html porte deja le ciel : cet ecran-ci le laisse donc voir
        // au lieu de poser un aplat par-dessus. Les deux se relaient sans a-coup.
        background: 'transparent',
      }}
    >
      <div
        className="respiration"
        style={{
          fontFamily: POLICE_TITRE,
          fontWeight: 200,
          fontSize: 52,
          letterSpacing: '-0.5px',
          color: C.encre,
        }}
      >
        Colette
      </div>
    </div>
  );
}

/** Attente d'une reponse du serveur. */
export function Chargement({ texte = 'Chargement…' }: { texte?: string }) {
  return (
    <div
      style={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 12,
        padding: 24,
      }}
    >
      <div className="rotation" />
      <div style={{ fontSize: 14, color: C.secondaire }}>{texte}</div>
    </div>
  );
}

/**
 * Attente d'une reponse, en SQUELETTE : des barres a la forme du contenu a venir, au
 * lieu d'un disque qui tourne. L'ecran ne « saute » plus quand les donnees arrivent.
 *
 * Le texte reste lu par les lecteurs d'ecran : un chargement muet n'est pas annonce.
 */
export function Squelette({
  lignes = 3,
  texte = 'Chargement…',
}: {
  lignes?: number;
  texte?: string;
}) {
  return (
    <div
      role="status"
      aria-label={texte}
      style={{ display: 'flex', flexDirection: 'column', gap: 10, padding: '4px 0' }}
    >
      {Array.from({ length: lignes }, (_, i) => (
        <div
          key={i}
          className="squelette"
          style={{
            height: i === 0 ? 58 : 74,
            borderRadius: 14,
            background: C.bordure,
          }}
        />
      ))}
    </div>
  );
}

/**
 * Panne de chargement. Toujours accompagnee d'un bouton pour reessayer : un ecran qui
 * annonce une erreur sans offrir de recours laisse l'utilisateur fermer l'application.
 */
export function Panne({ message, onReessayer }: { message: string; onReessayer: () => void }) {
  return (
    <div
      style={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 14,
        padding: 24,
        textAlign: 'center',
      }}
    >
      <div
        style={{
          background: C.corailFond,
          color: C.corailTexte,
          borderRadius: 12,
          padding: '12px 16px',
          fontSize: 14,
          fontWeight: 700,
        }}
      >
        {message}
      </div>
      <button
        type="button"
        onClick={onReessayer}
        style={{
          border: `2px solid ${C.violet}`,
          background: C.carte,
          color: C.violetTexte,
          borderRadius: 12,
          padding: '10px 20px',
          fontSize: 15,
          fontWeight: 800,
          fontFamily: 'inherit',
          cursor: 'pointer',
        }}
      >
        Réessayer
      </button>
    </div>
  );
}

/** Absence de contenu — a distinguer soigneusement d'une panne. */
export function Vide({ titre, texte }: { titre: string; texte: string }) {
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 6,
        padding: '38px 24px',
        textAlign: 'center',
      }}
    >
      <div style={{ fontSize: 16, fontWeight: 700, color: C.encre }}>{titre}</div>
      <div style={{ fontSize: 14, color: C.secondaire, lineHeight: 1.5 }}>{texte}</div>
    </div>
  );
}
